import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback, useEffect } from 'react';
import { Pressable, View } from 'react-native';

import { Text } from '@/components/ui/text';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import type { ToastData, ToastVariant } from './types';

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

interface VariantConfig {
  borderClass: string;
  iconClass: string;
  icon: React.ComponentProps<typeof Ionicons>['name'] | null;
}

const SWIPE_THRESHOLD = -40;
const VELOCITY_THRESHOLD = -500;
const DISMISS_TRANSLATE_Y = -200;

function getVariantConfig(variant: ToastVariant): VariantConfig {
  switch (variant) {
    case 'success':
      return {
        borderClass: 'bg-success',
        iconClass: 'text-success',
        icon: 'checkmark-circle',
      };
    case 'error':
      return {
        borderClass: 'bg-error',
        iconClass: 'text-error',
        icon: 'close-circle',
      };
    case 'warning':
      return {
        borderClass: 'bg-warning',
        iconClass: 'text-warning',
        icon: 'alert-circle',
      };
    case 'info':
      return {
        borderClass: 'bg-info',
        iconClass: 'text-info',
        icon: 'information-circle',
      };
    case 'default':
    default:
      return {
        borderClass: 'bg-border-visible',
        iconClass: 'text-muted-foreground',
        icon: null,
      };
  }
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const translateY = useSharedValue(-80);
  const opacity = useSharedValue(0);
  const gestureTranslateY = useSharedValue(0);
  const isDismissing = useSharedValue(false);

  const dismiss = useCallback(() => {
    onDismiss(toast.id);
  }, [onDismiss, toast.id]);

  // Enter animation
  useEffect(() => {
    translateY.set(withSpring(0, { damping: 20, stiffness: 300 }));
    opacity.set(withTiming(1, { duration: 200 }));
  }, [translateY, opacity]);

  // Auto-dismiss timer
  useEffect(() => {
    if (toast.duration === Infinity) return;

    const timer = setTimeout(() => {
      toast.onAutoClose?.();
      animateOut(dismiss);
    }, toast.duration);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id, toast.duration, toast.createdAt]);

  const animateOut = useCallback(
    (callback: () => void) => {
      'worklet';
      isDismissing.set(true);
      translateY.set(withTiming(
        DISMISS_TRANSLATE_Y,
        { duration: 200 },
        (finished) => {
          if (finished) {
            runOnJS(callback)();
          }
        },
      ));
      opacity.set(withTiming(0, { duration: 200 }));
    },
    [translateY, opacity, isDismissing],
  );

  const handleDismissPress = useCallback(() => {
    animateOut(dismiss);
  }, [animateOut, dismiss]);

  const panGesture = Gesture.Pan()
    .onUpdate((event) => {
      // Only allow upward swipes
      if (event.translationY < 0) {
        gestureTranslateY.set(event.translationY);
      }
    })
    .onEnd((event) => {
      if (
        event.translationY < SWIPE_THRESHOLD ||
        event.velocityY < VELOCITY_THRESHOLD
      ) {
        // Dismiss
        isDismissing.set(true);
        gestureTranslateY.set(withTiming(DISMISS_TRANSLATE_Y, { duration: 200 }));
        translateY.set(withTiming(DISMISS_TRANSLATE_Y, { duration: 200 }));
        opacity.set(withTiming(0, { duration: 200 }, (finished) => {
          if (finished) {
            runOnJS(dismiss)();
          }
        }));
      } else {
        // Spring back
        gestureTranslateY.set(withSpring(0, { damping: 20, stiffness: 300 }));
      }
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: translateY.get() + gestureTranslateY.get() },
    ],
    opacity: opacity.get(),
  }));

  const config = getVariantConfig(toast.variant);

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View
        className="mb-2 self-center"
        style={[animatedStyle, { maxWidth: 356, width: '100%' }]}
        accessibilityLiveRegion="polite"
      >
        <View
          className="bg-card rounded-xl overflow-hidden border border-border"
          style={{
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 8,
            elevation: 5,
          }}
          accessibilityRole="alert"
          accessibilityLabel={toast.title}
        >
          {/* Colored left accent border */}
          <View
            className={`absolute left-0 top-0 bottom-0 w-[3px] ${config.borderClass}`}
          />

          <View className="flex-row items-center pl-4 pr-3 py-3">
            {/* Icon */}
            {config.icon && (
              <Ionicons
                name={config.icon}
                size={18}
                className={`${config.iconClass} mr-2.5`}
                accessible={false}
              />
            )}

            {/* Text content */}
            <View className="flex-1 mr-2">
              <Text className="text-sm font-medium text-foreground">
                {toast.title}
              </Text>
              {toast.description ? (
                <Text className="text-xs text-muted-foreground mt-0.5">
                  {toast.description}
                </Text>
              ) : null}
            </View>

            {/* Action button */}
            {toast.action ? (
              <Pressable
                onPress={toast.action.onPress}
                className="ml-2 px-3 py-1.5 rounded-md border border-border bg-background"
              >
                <Text className="text-xs font-medium text-foreground">
                  {toast.action.label}
                </Text>
              </Pressable>
            ) : null}

            {/* Close button */}
            {toast.dismissible ? (
              <Pressable
                onPress={handleDismissPress}
                hitSlop={10}
              >
                <Ionicons
                  name="close"
                  size={16}
                  className="text-muted-foreground"
                  accessible={false}
                />
              </Pressable>
            ) : null}
          </View>
        </View>
      </Animated.View>
    </GestureDetector>
  );
}
